import React, {
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {IconChevronDown} from '../../Atoms';
import {Inline} from '../../Layout';
import {createInitialState, reducer} from './reducer';
import styles from './Accordion.module.css';

export type AccordionItemType = {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
};

export type AccordionHandle = {
  scrollToItem: (id: AccordionItemType['id']) => void;
};

const AccordionItem = ({
  item,
  expanded,
  isAnimating,
  onToggle,
  onTransitionEnd,
  headingLevel,
  hidden,
  ref,
}: {
  item: AccordionItemType;
  expanded: boolean;
  isAnimating: boolean;
  onToggle: () => void;
  onTransitionEnd: () => void;
  headingLevel: number;
  hidden?: boolean;
  ref?: React.Ref<HTMLDivElement | null>;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | undefined>();
  const triggerId = `trigger-${item.id}`;
  const contentId = `content-${item.id}`;

  useEffect(() => {
    const contentHeight =
      contentRef.current?.getBoundingClientRect().height ?? 0;

    setPanelHeight(expanded ? contentHeight : 0);
  }, [expanded]);

  return (
    <div className={clsx(styles.item, {[styles.hidden]: hidden})} ref={ref}>
      <div role="heading" aria-level={headingLevel}>
        <button
          id={triggerId}
          className={styles.trigger}
          type="button"
          aria-expanded={expanded}
          aria-controls={contentId}
          data-cy="accordion-trigger"
          onClick={onToggle}
        >
          <Inline space="md" justify="between" align="start">
            <span className={styles.text}>{item.title}</span>
            <IconChevronDown className={styles.icon} size={24} />
          </Inline>
        </button>
      </div>
      <div
        id={contentId}
        className={clsx(styles.panel, {
          [styles.expanded]: expanded && !isAnimating,
          [styles.collapsed]: !expanded && !isAnimating,
        })}
        style={{
          height: isAnimating ? panelHeight : undefined,
        }}
        role="region"
        aria-labelledby={triggerId}
        data-cy="accordion-content"
        onTransitionEnd={onTransitionEnd}
      >
        <div className={styles.content} ref={contentRef}>
          {item.content}
        </div>
      </div>
    </div>
  );
};

export const Accordion = ({
  items,
  headingLevel = 3,
  expandedItemId,
  visibleItems,
  ref,
}: {
  items: AccordionItemType[];
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  expandedItemId?: AccordionItemType['id'];
  visibleItems?: number;
  ref?: React.Ref<AccordionHandle>;
}) => {
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [state, dispatch] = useReducer(
    reducer,
    {items, initialExpandedItem: expandedItemId},
    createInitialState
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToItem: (id) => {
        itemRefs.current[id]?.scrollIntoView();
      },
    }),
    []
  );

  return items.map((item, index) => (
    <AccordionItem
      key={item.id}
      item={item}
      expanded={state[item.id].expanded}
      isAnimating={state[item.id].isAnimating}
      onToggle={() => {
        dispatch({type: 'TOGGLE', itemId: item.id});
      }}
      onTransitionEnd={() => {
        dispatch({type: 'SET_ANIMATION', itemId: item.id});
      }}
      headingLevel={headingLevel}
      ref={(el) => {
        itemRefs.current[item.id] = el;
      }}
      hidden={visibleItems !== undefined && index > visibleItems - 1}
    />
  ));
};
